// views/Settings/TreasuryThresholdsSettings.js
// The UGX amounts in the wallet service that decide when money needs a person:
// the reconciliation tolerances, the batch approval threshold, the manual
// payout tolerance and the rider cash on delivery limit. Each used to be an
// env var frozen at process start; the wallet now resolves WalletSetting row >
// env > shipped default at call time, so a change here applies within half a
// minute and without a redeploy. The server is the source of truth: the keys,
// the defaults and where each live value came from all arrive from the API.
import React, { useEffect, useState } from "react";
import { notify } from "../../utilities/notify";
import SettingsPanel from "../../components/SettingsPanel";
import { THEME as C } from "../../components/dashboardTheme";
import { apiWalletTreasuryThresholds, apiWalletUpdateTreasuryThresholds } from "../../api";
import { field, fmtWhen, ReadOnlyNotice, SaveButton } from "./settingsUi";
import SettingHelp from "./settingsHelp";

// Plain finance words for each key the wallet service exposes, with the
// tooltip that says what the wallet does with it (help/paymentsHelp.js). A key
// the server returns that is not listed here still renders, under its key name.
const ROWS = [
  {
    key: "recon_discrepancy_threshold_ugx",
    label: "Balance discrepancy threshold",
    tip: <SettingHelp topic="treasuryDiscrepancyThreshold" />,
    help: "A gap larger than this between the MTN balance and the balance the books explain flags the day on the reconciliation report.",
  },
  {
    key: "recon_interest_max_ugx",
    label: "Interest ceiling",
    tip: <SettingHelp topic="treasuryInterestCeiling" />,
    help: "On a day with no payouts and no money in, a rise up to this amount is booked as MTN interest; a larger rise is flagged for a person to explain.",
  },
  {
    key: "recon_fee_band_tolerance_ugx",
    label: "MTN fee band tolerance",
    tip: <SettingHelp topic="treasuryFeeBandTolerance" />,
    help: "Per payout. A larger difference between what MTN actually charged and the payout fee table is flagged so the table can be corrected.",
  },
  {
    key: "recon_delivery_tolerance_ugx",
    label: "Delivery fee reconciliation tolerance",
    tip: <SettingHelp topic="treasuryDeliveryTolerance" />,
    help: "Slack between an order's delivery fee and what the ledger split and paid out. Used only on a single order's settlement statement; no scheduled check reads it.",
  },
  {
    key: "batch_approval_threshold_ugx",
    label: "Batch approval threshold",
    tip: <SettingHelp topic="treasuryBatchApprovalThreshold" />,
    help: "Recorded on each rider and partner payout batch only. It changes nothing: every batch already waits for a super administrator's approval.",
  },
  {
    key: "manual_payout_tolerance_ugx",
    label: "Manual payout tolerance",
    tip: <SettingHelp topic="treasuryManualPayoutTolerance" />,
    help: "A manual payout further than this from the amount owed on the ledger is refused. Payouts with no owed figure are not checked.",
  },
  {
    key: "cod_rider_threshold_ugx",
    label: "Rider cash on delivery limit",
    tip: <SettingHelp topic="treasuryRiderCodLimit" />,
    help: "Stored only: riders are flagged by the dashboard server from each rider's own limit or its COD_DEFAULT_THRESHOLD, not from this value. The wallet refuses a value below 30,000.",
  },
];

const fmtUGX = (n) => (Number.isFinite(Number(n)) ? `UGX ${Number(n).toLocaleString("en-UG")}` : "");
const wholeNumber = (raw) => {
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 ? n : null;
};

const TreasuryThresholdsSettings = ({ canEdit = true }) => {
  const [live, setLive] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // The server's key list decides the rows; the labels above only name them.
  const keys = live ? Object.keys(live.values) : [];
  const rows = [
    ...ROWS.filter((r) => keys.includes(r.key)),
    ...keys.filter((k) => !ROWS.some((r) => r.key === k)).map((k) => ({ key: k, label: k, help: "" })),
  ];

  const applyLive = (data) => {
    setLive(data);
    setForm(Object.fromEntries(Object.keys(data.values).map((k) => [k, String(data.values[k] ?? "")])));
  };

  useEffect(() => {
    apiWalletTreasuryThresholds()
      .then(({ data }) => applyLive(data))
      .catch((err) => notify.fromError(err, "Could not load the treasury thresholds"))
      .finally(() => setLoading(false));
  }, []);

  // A blank field is left alone, so only filled fields that differ from the
  // live value are sent. A filled field must be a whole number of UGX.
  const filled = keys.filter((k) => String(form[k] ?? "").trim() !== "");
  const invalid = filled.filter((k) => wholeNumber(form[k]) === null);
  const changes = Object.fromEntries(
    filled
      .filter((k) => wholeNumber(form[k]) !== null && wholeNumber(form[k]) !== live.values[k])
      .map((k) => [k, wholeNumber(form[k])])
  );
  const changedKeys = Object.keys(changes);

  const save = async (e) => {
    e.preventDefault();
    if (invalid.length) return notify.error("Check the amounts", "Each threshold is a whole number of UGX, zero or more.");
    if (!changedKeys.length) return;
    setSaving(true);
    try {
      const { data } = await apiWalletUpdateTreasuryThresholds(changes);
      applyLive(data);
      notify.success(
        `${changedKeys.length === 1 ? "Threshold" : `${changedKeys.length} thresholds`} saved`,
        "The wallet applies the new amounts within 30 seconds; no redeploy needed."
      );
    } catch (err) {
      notify.fromError(err, "Could not save the treasury thresholds");
    } finally {
      setSaving(false);
    }
  };

  const defaultHint = (k) => {
    if (!live) return "";
    const origin = live.defaultSources?.[k] === "env" ? "hosting environment" : "shipped";
    return `Default ${fmtUGX(live.defaults?.[k])} (${origin})`;
  };
  const lastChanged = (k) => {
    const row = live?.lastChanged?.[k];
    if (!row || !row.updatedAt) return live?.sources?.[k] === "env" ? "From the hosting environment; never changed here" : "Never changed here";
    return `Last changed by ${row.updatedBy || "a staff member"} on ${fmtWhen(row.updatedAt)}`;
  };

  return (
    <SettingsPanel
      icon="fa-scale-balanced"
      title="Treasury thresholds"
      subtitle={loading ? "Loading..." : "The amounts, in UGX, at which the wallet's reconciliation and payout checks stop and ask a person"}
    >
      {!canEdit && <ReadOnlyNotice level="manage" />}
      <form onSubmit={save}>
        <SettingsPanel.Section title={<>Thresholds<SettingHelp topic="treasuryThresholds" /></>} icon="fa-coins">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                {["Threshold", "Amount (UGX)", "Default", "Last changed"].map((h) => (
                  <th key={h} style={{ textAlign: "left", fontSize: 11, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.06em", padding: "6px 8px", borderBottom: `1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <td style={{ padding: "8px", verticalAlign: "top", maxWidth: 360 }}>
                    <div style={{ fontWeight: 600, color: C.text }}>{r.label}{r.tip || null}</div>
                    {r.help && <div style={field.help}>{r.help}</div>}
                  </td>
                  <td style={{ padding: "8px", verticalAlign: "top", minWidth: 150 }}>
                    <input
                      style={{ ...field.input, borderColor: invalid.includes(r.key) ? C.red : C.border }}
                      type="number"
                      min="0"
                      step="1"
                      inputMode="numeric"
                      aria-label={r.label}
                      value={form[r.key] ?? ""}
                      disabled={!canEdit || saving || loading}
                      onChange={(e) => setForm((f) => ({ ...f, [r.key]: e.target.value }))}
                    />
                    <div style={field.help}>
                      {live?.sources?.[r.key] === "setting" ? "Set on the dashboard" : "Using the default"}
                    </div>
                  </td>
                  <td style={{ padding: "8px", verticalAlign: "top", color: C.textLight, fontSize: 12, whiteSpace: "nowrap" }}>
                    {defaultHint(r.key)}
                  </td>
                  <td style={{ padding: "8px", verticalAlign: "top", color: C.textLight, fontSize: 12 }}>
                    {live ? lastChanged(r.key) : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={field.actions}>
            {canEdit && <SaveButton saving={saving} disabled={loading || invalid.length > 0 || changedKeys.length === 0} />}
            {canEdit && changedKeys.length > 0 && (
              <span style={{ fontSize: 12, color: C.textLight }}>
                {changedKeys.length === 1 ? "1 threshold" : `${changedKeys.length} thresholds`} will change. Blank fields are left as they are.
              </span>
            )}
          </div>
        </SettingsPanel.Section>
      </form>
    </SettingsPanel>
  );
};

export default TreasuryThresholdsSettings;
